import { makeStyles } from '@material-ui/styles'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { humanReadableSize } from 'app/plugins/openstack/helpers'
import Progress from 'core/components/progress/Progress'
import SimpleLink from 'core/components/SimpleLink'
import Grid from 'core/elements/grid'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { IHostDetailsPageTabs } from 'openstack/components/infrastructure/hosts/host-details/model'
import { resmgrHostsByIdSelector } from 'openstack/components/infrastructure/selectors'
import { IVolumeSnapshotDetailsPageTabs } from 'openstack/components/storage/snapshots/details/model'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import OverviewCard from 'openstack/components/vms/vm-details/OverviewCard'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { isNilOrEmpty } from 'utils/fp'
import { formatDate } from 'utils/misc'
import { formatTimestampToLocal } from '../../../vms/helpers'
import { listCinderMessagesForVolume } from '../../messages/actions'
import { cinderMessagesForVolumeSelector } from '../../messages/selectors'
import { StatusCellComponent as SnapshotStatusCellComponent } from '../../snapshots/VolumeSnapshotsListPage'
import VolumeErrorModal from '../VolumeErrorModal'
import { StatusCellComponent } from '../VolumesListPage'
import { VolumeSourceCell } from './VolumeSourceCell'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  overview: {
    display: 'grid',
    gridTemplateColumns: '370px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
  },
}))

const getVolumeSnapshotColumns = () => [
  {
    key: 'name',
    label: 'Name',
    render: (name, item) => (
      <SimpleLink
        src={routeManager.routes.openstack.volumeSnapshotDetails.path({
          id: item?.id,
          tab: IVolumeSnapshotDetailsPageTabs.Overview,
        })}
      >
        {name}
      </SimpleLink>
    ),
  },
  {
    key: 'id',
    label: 'UUID',
  },
  {
    key: 'description',
    label: 'Description',
  },
  {
    key: 'status',
    label: 'Status',
    CellComponent: SnapshotStatusCellComponent,
  },
  {
    key: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
  },
]

export default function Overview({ volume, loading }) {
  const classes = useStyles({})
  const [showErrorModal, setShowErrorModal] = useState(false)
  const { loading: loadingResmgrHosts } = useListAction(listResmgrHosts)
  const hostsById = useSelector(resmgrHostsByIdSelector)
  const { reload: reloadMessages } = useListAction(listCinderMessagesForVolume, {
    params: { volumeUuid: volume?.id },
    loadOnDemand: true,
  })
  const messages = useSelectorWithParams(cinderMessagesForVolumeSelector, {
    volumeUuid: volume?.id,
  })

  useEffect(() => {
    if (volume?.id && volume?.status === 'error') {
      reloadMessages()
    }
  }, [volume?.id, volume?.status, reloadMessages])

  const volumeErrorMessage = useMemo(() => {
    if (!messages || messages.length === 0) return null
    return (
      messages.find((msg) => msg?.resource_type === 'VOLUME' && msg?.message_level === 'ERROR') ||
      null
    )
  }, [messages])
  const volumeSnapshotTableColumns = useMemo(() => getVolumeSnapshotColumns(), [])

  const overviewData = useMemo(
    () =>
      volume
        ? [
            {
              key: 'Name',
              value: volume?.name,
            },
            {
              key: 'UUID',
              value: volume?.id,
            },
            {
              key: 'Management Host',
              value: volume?.hostId,
              render: ({ hostId }) =>
                hostsById?.[hostId]?.name || hostId ? (
                  <SimpleLink
                    src={routeManager.routes.openstack.hostDetails.path({
                      id: hostId,
                      tab: IHostDetailsPageTabs.Overview,
                    })}
                  >
                    {hostsById?.[hostId]?.name || hostId}
                  </SimpleLink>
                ) : null,
              condition: (value) => isAdmin(),
            },
            {
              key: 'Storage Backend',
              value: volume?.backend,
            },
            {
              key: 'Status',
              value: volume?.status,
              render: (vol) => (
                <StatusCellComponent
                  value={volume?.status}
                  item={vol}
                  setErrorVolume={() => setShowErrorModal(true)}
                />
              ),
            },
            {
              key: 'Type',
              value: volume?.volume_type,
            },
            {
              key: 'Description',
              value: volume?.description,
            },
            {
              key: 'Attached VM',
              value: volume?.attachedVm,
              render: ({ attachedVm }) =>
                attachedVm ? (
                  <SimpleLink
                    src={routeManager.routes.openstack.vmDetails.path({
                      id: attachedVm?.id,
                      tab: IVmDetailsPageTabs.Overview,
                    })}
                  >
                    {attachedVm?.name}
                  </SimpleLink>
                ) : null,
            },
            {
              key: 'device',
              label: 'Device',
              value: volume?.device,
            },
            {
              key: 'Size',
              value: volume?.size,
              render: ({ size }) => humanReadableSize(size * 1024 * 1024 * 1024),
            },
            {
              key: 'Bootable',
              value: volume?.bootable,
            },
            {
              key: 'Multiattach',
              value: volume?.multiattach,
              render: ({ multiattach }) => (multiattach ? 'True' : 'False'),
            },
            {
              key: 'Source',
              value: <VolumeSourceCell item={volume} />,
              info: 'The resource used to create this volume. Blank if no resource used',
            },
            {
              key: 'Migration Status',
              value: volume?.migration_status,
            },
            {
              key: 'Created',
              value: volume?.created_at,
              render: ({ created_at }) => formatDate(created_at),
            },
            {
              key: 'Metadata',
              value: volume?.metadata,
              render: ({ metadata }) => {
                return !isNilOrEmpty(metadata) ? (
                  <Labels labels={metadata} variant="default" showMoreButton ellipsisAt={75} />
                ) : (
                  <Text variant="caption1">No Metadata</Text>
                )
              },
            },
            {
              key: 'Image Metadata',
              value: volume?.volume_image_metadata,
              render: ({ volume_image_metadata }) => {
                return !isNilOrEmpty(volume_image_metadata) ? (
                  <Labels
                    labels={volume_image_metadata}
                    variant="default"
                    showMoreButton
                    ellipsisAt={35}
                  />
                ) : (
                  <Text variant="caption1">No Metadata</Text>
                )
              },
            },
          ]
        : [],
    [hostsById, volume, volumeErrorMessage],
  )

  return (
    <Progress loading={loading}>
      <div className={classes.overview}>
        <div className={classes.column}>
          <OverviewCard item={volume} data={overviewData} icon="coins" />
        </div>
        <div className={classes.column}>
          <Grid
            uniqueIdentifier="id"
            data={volume?.snapshots || []}
            columns={volumeSnapshotTableColumns}
            loading={loading}
            compact
            label="Volume Snapshots"
            showItemsCountInLabel={false}
          />
        </div>
      </div>
      {showErrorModal && volume?.status === 'error' && volume?.id && (
        <VolumeErrorModal volume={volume} onClose={() => setShowErrorModal(false)} />
      )}
    </Progress>
  )
}
